import React from 'react';
import { AcceptBlock, inputBlock, LangBlock } from '../../global';
import { SystemShared } from '../../../../shared/system';
import { UserDataWeb } from '../../../../shared/userdataweb';
import { CURRENCY_NAME } from '../../../../shared/currency';
import { WebStorage } from '../../../storage';
import { AdminDataTable } from './admin.table';
import { AdminPagination } from './admin.pagination';
import { usePaginatedTable } from './usePaginatedTable';
import {
    AdminButton,
    AdminCard,
    AdminFormField,
    AdminFormGrid,
    AdminPageWrap,
    AdminSection,
} from './admin.ui';

type PromoRow = {
    id: number;
    code: string;
    remain: number;
    used: number;
    amount: number;
    currency: CURRENCY_NAME;
    created: number;
};

type PromoUsedRow = {
    id: number;
    code: string;
    who: number;
    time: number;
    created?: number;
};

type Props = {
    promoList: PromoRow[];
    promoListUsed: PromoUsedRow[];
    usersList: UserDataWeb[];
    loadPromoList: () => void;
};

const downloadPromoCodes = (
    data: PromoRow[],
    labels: { remain: string; amount: string; currency: string },
) => {
    let content = '';

    data.forEach((q) => {
        content += `\n${q.code}`;
    });

    content += `\n\n\n`;
    content += `${labels.remain}: ${data[0].remain}\n`;
    content += `${labels.amount}: ${data[0].amount}\n`;
    content += `${labels.currency}: ${data[0].currency}\n`;

    const link = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });

    link.href = URL.createObjectURL(file);
    link.download = 'promocodes.txt';
    link.click();
    URL.revokeObjectURL(link.href);
};

export const AdminPromo = ({ promoList, promoListUsed, usersList, loadPromoList }: Props) => {
    const [, , LangString] = LangBlock();
    const activeTable = usePaginatedTable(promoList, 50);
    const usedTable = usePaginatedTable(promoListUsed, 50);

    const [promoCreate, setPromoCreate] = React.useState({
        use: 1,
        amount: 1,
        count: 1,
    });

    const [promoCreateName, setPromoCreateName] = React.useState({
        use: 1,
        amount: 1,
        name: '',
    });

    const userLabel = (id: number) => {
        const rec = usersList.find((q) => q.id === id);

        return rec ? `${rec.name} (${rec.id})` : `${id}`;
    };

    return (
        <AdminPageWrap>
            <AdminSection
                title={LangString('AdminPromoTitle')}
                subtitle={LangString('AdminPromoSubtitle')}
            >
                <AdminCard title={LangString('AdminActivePromos', promoList.length)}>
                    <AdminDataTable
                        data={activeTable.pageData}
                        rowKey={(row) => row.id}
                        emptyMessage={LangString('AdminNoPromos')}
                        columns={[
                            {
                                key: 'code',
                                label: LangString('AdminCode'),
                                primary: true,
                                render: (row) => row.code,
                            },
                            {
                                key: 'id',
                                label: LangString('AdminId'),
                                render: (row) => row.id,
                            },
                            {
                                key: 'remain',
                                label: LangString('AdminRemain'),
                                render: (row) => row.remain,
                            },
                            {
                                key: 'used',
                                label: LangString('AdminUsed'),
                                render: (row) => row.used,
                            },
                            {
                                key: 'amount',
                                label: LangString('AdminAmount'),
                                render: (row) => `${row.amount} ${row.currency}`,
                            },
                            {
                                key: 'created',
                                label: LangString('AdminCreated'),
                                render: (row) => SystemShared.timeStampString(row.created),
                            },
                            {
                                key: 'actions',
                                label: LangString('AdminActions'),
                                render: (row) => (
                                    <AdminButton
                                        variant="danger"
                                        size="sm"
                                        onClick={async (e) => {
                                            e.preventDefault();

                                            if (!(await AcceptBlock())) {
                                                return;
                                            }

                                            WebStorage.ApiRequest('/api/removePromo', {
                                                promo: row.id,
                                            }).then((res) => {
                                                if (res.status) {
                                                    loadPromoList();
                                                }
                                            });
                                        }}
                                    >
                                        {LangString('AdminRemove')}
                                    </AdminButton>
                                ),
                            },
                        ]}
                    />
                    <AdminPagination
                        page={activeTable.page}
                        totalPages={activeTable.totalPages}
                        total={activeTable.total}
                        limit={activeTable.limit}
                        onPageChange={activeTable.setPage}
                        onLimitChange={activeTable.setLimit}
                    />
                </AdminCard>

                <AdminCard title={LangString('AdminUsedPromos', promoListUsed.length)}>
                    <AdminDataTable
                        data={usedTable.pageData}
                        rowKey={(row) => row.id}
                        emptyMessage={LangString('AdminNoUsedPromos')}
                        columns={[
                            {
                                key: 'code',
                                label: LangString('AdminCode'),
                                primary: true,
                                render: (row) => row.code,
                            },
                            {
                                key: 'time',
                                label: LangString('AdminTime'),
                                render: (row) =>
                                    SystemShared.timeStampString(row.time ?? row.created ?? 0),
                            },
                            {
                                key: 'who',
                                label: LangString('AdminUser'),
                                render: (row) => userLabel(row.who),
                            },
                        ]}
                    />
                    <AdminPagination
                        page={usedTable.page}
                        totalPages={usedTable.totalPages}
                        total={usedTable.total}
                        limit={usedTable.limit}
                        onPageChange={usedTable.setPage}
                        onLimitChange={usedTable.setLimit}
                    />
                </AdminCard>

                <AdminGridCreate
                    promoCreate={promoCreate}
                    setPromoCreate={setPromoCreate}
                    promoCreateName={promoCreateName}
                    setPromoCreateName={setPromoCreateName}
                    loadPromoList={loadPromoList}
                    downloadPromoCodes={downloadPromoCodes}
                />
            </AdminSection>
        </AdminPageWrap>
    );
};

const AdminGridCreate = ({
    promoCreate,
    setPromoCreate,
    promoCreateName,
    setPromoCreateName,
    loadPromoList,
    downloadPromoCodes,
}: {
    promoCreate: { use: number; amount: number; count: number };
    setPromoCreate: React.Dispatch<
        React.SetStateAction<{ use: number; amount: number; count: number }>
    >;
    promoCreateName: { use: number; amount: number; name: string };
    setPromoCreateName: React.Dispatch<
        React.SetStateAction<{ use: number; amount: number; name: string }>
    >;
    loadPromoList: () => void;
    downloadPromoCodes: (
        data: PromoRow[],
        labels: { remain: string; amount: string; currency: string },
    ) => void;
}) => {
    const [, , LangString] = LangBlock();
    const downloadLabels = {
        remain: LangString('AdminRemain'),
        amount: LangString('AdminAmount'),
        currency: LangString('AdminCurrency'),
    };

    return (
    <div className="admin-promo-create">
        <div className="admin-card">
            <div className="admin-card__title">{LangString('AdminCreateMultiple')}</div>
            <div className="admin-card__body">
                <AdminFormGrid>
                    <AdminFormField label={LangString('AdminCountUse')} wide>
                        {inputBlock(
                            'number',
                            promoCreate.use,
                            (val) => setPromoCreate((old) => ({ ...old, use: val })),
                            LangString('AdminCountUse'),
                        )}
                    </AdminFormField>
                    <AdminFormField label={LangString('AdminAmountUsd')} wide>
                        {inputBlock(
                            'number',
                            promoCreate.amount,
                            (val) => setPromoCreate((old) => ({ ...old, amount: val })),
                            LangString('AdminAmountUsdPlaceholder'),
                        )}
                    </AdminFormField>
                    <AdminFormField label={LangString('AdminHowMany')} wide>
                        {inputBlock(
                            'number',
                            promoCreate.count,
                            (val) => setPromoCreate((old) => ({ ...old, count: val })),
                            LangString('AdminHowManyPlaceholder'),
                        )}
                    </AdminFormField>
                </AdminFormGrid>
                <AdminButton
                    variant="primary"
                    block
                    onClick={(e) => {
                        e.preventDefault();

                        WebStorage.ApiRequest('/api/admin/promos/create', {
                            ...promoCreate,
                        }).then((res) => {
                            if (res.status) {
                                loadPromoList();
                                downloadPromoCodes(res.data, downloadLabels);
                            }
                        });
                    }}
                >
                    {LangString('AdminCreatePromos')}
                </AdminButton>
            </div>
        </div>

        <div className="admin-card">
            <div className="admin-card__title">{LangString('AdminCreateNamed')}</div>
            <div className="admin-card__body">
                <AdminFormGrid>
                    <AdminFormField label={LangString('AdminCountUse')} wide>
                        {inputBlock(
                            'number',
                            promoCreateName.use,
                            (val) => setPromoCreateName((old) => ({ ...old, use: val })),
                            LangString('AdminCountUse'),
                        )}
                    </AdminFormField>
                    <AdminFormField label={LangString('AdminAmountUsd')} wide>
                        {inputBlock(
                            'number',
                            promoCreateName.amount,
                            (val) => setPromoCreateName((old) => ({ ...old, amount: val })),
                            LangString('AdminAmountUsdPlaceholder'),
                        )}
                    </AdminFormField>
                    <AdminFormField label={LangString('AdminPromoName')} wide>
                        {inputBlock(
                            'text',
                            promoCreateName.name,
                            (val) => setPromoCreateName((old) => ({ ...old, name: val })),
                            LangString('AdminPromoName'),
                        )}
                    </AdminFormField>
                </AdminFormGrid>
                <AdminButton
                    variant="primary"
                    block
                    onClick={(e) => {
                        e.preventDefault();

                        WebStorage.ApiRequest('/api/admin/promos/createName', {
                            ...promoCreateName,
                        }).then((res) => {
                            if (res.status) {
                                loadPromoList();
                                downloadPromoCodes(res.data, downloadLabels);
                            }
                        });
                    }}
                >
                    {LangString('AdminCreatePromo')}
                </AdminButton>
            </div>
        </div>
    </div>
    );
};
